"use client";

import { useEffect, useRef, useState } from "react";

import GoogleMaps from "@/components/shared/GoogleMaps";

interface TourPageDeferredMapProps {
  markers: {
    lat: number;
    lng: number;
  }[];
  center?: {
    lat: number;
    lng: number;
  };
  containerStyle: {
    width: string;
    height: string;
  };
  gbpLink?: string | null;
  className?: string;
  linkClassName?: string;
  placeholderClassName?: string;
  title?: string;
  iframeTabIndex?: number;
}

export default function TourPageDeferredMap({
  containerStyle,
  markers,
  center,
  gbpLink,
  className,
  linkClassName,
  placeholderClassName,
  title = "Google map",
  iframeTabIndex,
}: TourPageDeferredMapProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [shouldLoadMap, setShouldLoadMap] = useState(false);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || shouldLoadMap) return;

    if (!("IntersectionObserver" in window)) {
      setShouldLoadMap(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShouldLoadMap(true);
        observer.disconnect();
      },
      { rootMargin: "1000px 0px" },
    );

    observer.observe(root);

    return () => observer.disconnect();
  }, [shouldLoadMap]);

  return (
    <div
      ref={rootRef}
      data-tour-map-deferred={shouldLoadMap ? "loaded" : "pending"}
    >
      {shouldLoadMap ? (
        <GoogleMaps
          containerStyle={containerStyle}
          markers={markers}
          center={center}
          gbpLink={gbpLink}
          className={className}
          linkClassName={linkClassName}
          title={title}
          iframeTabIndex={iframeTabIndex}
        />
      ) : (
        <div className={`w-full overflow-hidden ${className ?? ""}`}>
          <div
            role="img"
            aria-label={title}
            data-tour-map-placeholder
            style={containerStyle}
            className={`block w-full ${placeholderClassName ?? "bg-slate-100"}`}
          />
          {gbpLink ? (
            <a
              href={gbpLink}
              target="_blank"
              rel="nofollow noopener"
              className={
                linkClassName ??
                "mt-4 block text-center font-semibold text-primary-blue underline hover:text-primary-blue/80"
              }
            >
              Open in Google Maps
            </a>
          ) : null}
        </div>
      )}
    </div>
  );
}
